import { ELanguage } from '@ringana/configs';
import ZApiService from './ZApiService';
import {
  IBlogEntry,
  IBox,
  INewsletterFormData,
  IQuickLink,
  ISlide,
  ISocialMedia,
} from '../common/interfaces/frontpage.interface';
import FrontpageHelper from '../helpers/page-specific/FrontpageHelper';
import FApiService from './FApiService';
import WpApiService from './WpApiService';
import CookieHelper from '../helpers/CookieHelper';

export default class FrontpageService {
  private zApiService: ZApiService;
  private fApiService: FApiService;
  private wpApiService: WpApiService;

  public constructor(zApiService: ZApiService, fApiService: FApiService, wpApiService: WpApiService) {
    this.zApiService = zApiService;
    this.fApiService = fApiService;
    this.wpApiService = wpApiService;
  }

  public async subscribeToNewsletter(data: INewsletterFormData): Promise<boolean> {
    const transformedData = FrontpageHelper.transformNewsletterFormData(data);
    return this.zApiService.updateNewsletter(transformedData);
  }

  public async getBlogEntries(): Promise<IBlogEntry[]> {
    const apiBlogEntries = await this.fApiService.getBlogEntries();
    return Promise.all(apiBlogEntries.map(async (apiBlogEntry): Promise<IBlogEntry> => {
      const link = await this.getLanguageLinkOrDefaultLink(apiBlogEntry.pageId);
      return FrontpageHelper.transformApiBlogEntryToBlogEntry(apiBlogEntry, link);
    }));
  }

  public async getBoxes(): Promise<IBox[]> {
    const apiBoxes = await this.fApiService.getBoxes();
    return Promise.all(apiBoxes.map(async (apiBox): Promise<IBox> => {
      const link = await this.getLanguageLinkOrDefaultLink(apiBox.pageId);
      return FrontpageHelper.transformApiBoxToBox(apiBox, link);
    }));
  }

  public async getQuickLinks(): Promise<IQuickLink[]> {
    const apiQuickLinks = await this.fApiService.getQuickLinks();
    return Promise.all(apiQuickLinks.map(async (apiQuickLink): Promise<IQuickLink> => {
      const link = await this.getLanguageLinkOrDefaultLink(apiQuickLink.pageId);
      return FrontpageHelper.transformApiQuickLinkToQuickLink(apiQuickLink, link);
    }));
  }

  public async getSlides(): Promise<ISlide[]> {
    const apiSlides = await this.fApiService.getSlides();
    return Promise.all(apiSlides.map(async (apiSlide): Promise<ISlide> => {
      const link = await this.getLanguageLinkOrDefaultLink(apiSlide.pageId);
      return FrontpageHelper.transformApiSlideToSlide(apiSlide, link);
    }));
  }

  public async getSocialMedias(): Promise<ISocialMedia[]> {
    const apiSocialMedias = await this.fApiService.getSocialMedias();
    return apiSocialMedias.map(apiSocialMedia => {
      return FrontpageHelper.transformApiSocialMediaToSocialMedia(apiSocialMedia);
    });
  }

  private async getLanguageLinkOrDefaultLink(pageId: number): Promise<string | undefined> {
    if(pageId < 1) {
      return undefined;
    }
    const defaultLanguage = ELanguage.DE;
    const language = CookieHelper.getLanguage() as ELanguage || defaultLanguage;
    const wpPagesForPageId = await this.wpApiService.getWpPageUrls(pageId);
    if(wpPagesForPageId[language] && wpPagesForPageId[language].url) {
      return wpPagesForPageId[language].url as string;
    }
    if(wpPagesForPageId[defaultLanguage] && wpPagesForPageId[defaultLanguage].url) {
      return wpPagesForPageId[defaultLanguage].url as string;
    }
    return undefined;
  }
}
